<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else-if="coupon" class="detail-container">
      <!-- 狀態卡片 -->
      <div class="status-card">
        <div class="status-icon" :class="getStatusClass(coupon.status)">
          <i :class="getStatusIcon(coupon.status)"></i>
        </div>
        <div class="status-content">
          <div class="status-label">優惠券狀態</div>
          <div class="status-value">
            <span :class="getStatusBadgeClass(coupon.status)">
              {{ getStatusText(coupon.status) }}
            </span>
          </div>
        </div>
        <div class="coupon-code-display">
          <div class="code-label">優惠券代碼</div>
          <code class="coupon-code">{{ coupon.couponCode }}</code>
        </div>
      </div>

      <!-- 基本資訊 -->
      <div class="info-section">
        <h2 class="section-title">
          <i class="ri-file-list-line"></i>
          基本資訊
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>優惠券模板</label>
            <div>
              <span :class="getCouponTypeBadgeClass(coupon.couponType)">
                {{ coupon.templateName }}
              </span>
            </div>
          </div>
          <div class="info-item">
            <label>優惠券類型</label>
            <div>
              <span :class="getCouponTypeBadgeClass(coupon.couponType)">
                {{ getCouponTypeText(coupon.couponType) }}
              </span>
            </div>
          </div>
          <div class="info-item">
            <label>折扣值</label>
            <div>{{ formatDiscountValue(coupon) }}</div>
          </div>
          <div class="info-item">
            <label>最低消費門檻</label>
            <div>{{ formatCurrency(coupon.minPurchaseAmount) }}</div>
          </div>
          <div class="info-item">
            <label>發放時間</label>
            <div>{{ formatDateTime(coupon.issuedAt) }}</div>
          </div>
          <div class="info-item">
            <label>有效期</label>
            <div>
              <div v-if="coupon.validFrom && coupon.validUntil">
                {{ formatDateTime(coupon.validFrom) }} ~ {{ formatDateTime(coupon.validUntil) }}
              </div>
              <span v-else class="text-muted">-</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 會員資訊 -->
      <div v-if="coupon.memberId" class="info-section">
        <h2 class="section-title">
          <i class="ri-user-line"></i>
          會員資訊
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>會員姓名</label>
            <div>{{ coupon.memberName || '-' }}</div>
          </div>
          <div class="info-item">
            <label>電話</label>
            <div>{{ coupon.memberPhone || '-' }}</div>
          </div>
          <div class="info-item">
            <label>Email</label>
            <div>{{ coupon.memberEmail || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 使用資訊 -->
      <div v-if="coupon.status === 'used'" class="info-section">
        <h2 class="section-title">
          <i class="ri-checkbox-circle-line"></i>
          使用資訊
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>使用時間</label>
            <div>{{ formatDateTime(coupon.usedAt) }}</div>
          </div>
          <div class="info-item">
            <label>折扣金額</label>
            <div>{{ formatCurrency(coupon.discountAmount) }}</div>
          </div>
          <div v-if="coupon.usedOrderId" class="info-item">
            <label>訂單 ID</label>
            <div>
              <code>{{ coupon.usedOrderId }}</code>
            </div>
          </div>
        </div>
      </div>

      <!-- 過期資訊 -->
      <div v-if="coupon.status === 'expired'" class="info-section">
        <h2 class="section-title">
          <i class="ri-time-line"></i>
          過期資訊
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>過期時間</label>
            <div>{{ formatDateTime(coupon.expiredAt) }}</div>
          </div>
        </div>
      </div>

      <!-- 取消資訊 -->
      <div v-if="coupon.status === 'cancelled'" class="info-section">
        <h2 class="section-title">
          <i class="ri-close-circle-line"></i>
          取消資訊
        </h2>
        <div class="info-grid">
          <div class="info-item">
            <label>取消時間</label>
            <div>{{ formatDateTime(coupon.cancelledAt) }}</div>
          </div>
          <div v-if="coupon.cancelReason" class="info-item">
            <label>取消原因</label>
            <div>{{ coupon.cancelReason }}</div>
          </div>
        </div>
      </div>

      <!-- 發放資訊 -->
      <div class="info-section">
        <h2 class="section-title">
          <i class="ri-information-line"></i>
          發放資訊
        </h2>
        <div class="info-grid">
          <div v-if="coupon.issueSource" class="info-item">
            <label>發放來源</label>
            <div>{{ coupon.issueSource }}</div>
          </div>
          <div v-if="coupon.issueMethod" class="info-item">
            <label>發放方式</label>
            <div>{{ coupon.issueMethod }}</div>
          </div>
          <div v-if="coupon.issueReason" class="info-item">
            <label>發放原因</label>
            <div>{{ coupon.issueReason }}</div>
          </div>
        </div>
      </div>

      <!-- QR Code -->
      <div v-if="coupon.qrCodeUrl" class="info-section">
        <h2 class="section-title">
          <i class="ri-qr-code-line"></i>
          QR Code
        </h2>
        <div class="qr-code-container">
          <img :src="coupon.qrCodeUrl" alt="優惠券 QR Code" class="qr-code-image" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'CouponIssueDetail',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('優惠券詳情');

    const coupon = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const fetchCoupon = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await couponService.getCouponById(route.params.id);

        if (response && response.success && response.data) {
          coupon.value = response.data;
        } else {
          error.value = response?.message || '獲取優惠券詳情失敗';
        }
      } catch (err) {
        console.error('獲取優惠券詳情失敗:', err);
        error.value = err.message || '獲取優惠券詳情失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleBack = () => {
      router.back();
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '$0';
      return `$${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatDiscountValue = (coupon) => {
      if (coupon.couponType === 'discount') {
        return `${coupon.discountValue}%`;
      } else if (coupon.couponType === 'fixed_amount') {
        return formatCurrency(coupon.discountValue);
      } else if (coupon.couponType === 'free_shipping') {
        return '免運費';
      } else if (coupon.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        available: '可用',
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        available: 'badge badge-success',
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-primary',
        expired: 'badge badge-secondary',
        cancelled: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getStatusClass = (status) => {
      const classMap = {
        available: 'success',
        unused: 'success',
        transferring: 'warning',
        transferred_out: 'info',
        used: 'primary',
        expired: 'secondary',
        cancelled: 'danger'
      };
      return classMap[status] || 'secondary';
    };

    const getStatusIcon = (status) => {
      const iconMap = {
        available: 'ri-checkbox-circle-line',
        unused: 'ri-checkbox-circle-line',
        transferring: 'ri-send-plane-line',
        transferred_out: 'ri-gift-line',
        used: 'ri-check-line',
        expired: 'ri-time-line',
        cancelled: 'ri-close-circle-line'
      };
      return iconMap[status] || 'ri-file-line';
    };

    onMounted(() => {
      fetchCoupon();
    });

    return {
      coupon,
      loading,
      error,
      pageTitle,
      handleBack,
      formatDateTime,
      formatCurrency,
      formatDiscountValue,
      getCouponTypeText,
      getCouponTypeBadgeClass,
      getStatusText,
      getStatusBadgeClass,
      getStatusClass,
      getStatusIcon
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_detail-common.scss';

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.status-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.status-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.status-icon.success {
  background: #d4edda;
  color: #28a745;
}

.status-icon.primary {
  background: #cfe2ff;
  color: #0d6efd;
}

.status-icon.secondary {
  background: #e2e3e5;
  color: #6c757d;
}

.status-icon.danger {
  background: #f8d7da;
  color: #dc3545;
}

.status-content {
  flex: 1;
}

.status-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.status-value {
  font-size: 20px;
  font-weight: 600;
}

.coupon-code-display {
  text-align: right;
}

.code-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.coupon-code {
  background: #f0f0f0;
  padding: 8px 16px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: inline-block;
}

.info-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.info-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.info-item div {
  font-size: 16px;
  color: #333;
}

.text-muted {
  color: #999;
}

.qr-code-container {
  text-align: center;
  padding: 20px;
}

.qr-code-image {
  max-width: 300px;
  height: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: white;
}
</style>
